import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Divider, Flex, Typography} from 'antd';
import {DownloadOutlined, FolderOpenTwoTone} from '@ant-design/icons';
import {ModelDetailDTO, ModelDTO} from 'hemwb-api';
import {useModelFileTreeLoader, TreeNode, modelFileItemDownload} from '../../../../store/modelFile';
import {modelDownload} from '../../../../store/model';
import {downloadFromResponse} from '../../../../utils/download';
import {trackModelItemDownloaded} from '../../../../tracking/tracking';
import {ModelFileDownloadModal} from './ModelFileDownloadModal';
import {FileTreeNode} from './FileTreeNode';
import styles from './ModelFilesTab.module.scss';
import {canDownloadModelFiles} from '../../../../permissions/usePermissions';
import {PERMISSION} from '../../../../permissions/types';

interface ModelFilesTabProps {
  model: ModelDTO | null;
}

// Define filter arrays outside component to prevent re-renders
const FILTER_TAGS = ['Parameter'];

/**
 * Regular Model Files tab - displays model files from /treeaudited endpoint
 * No THEOREM-specific logic - regular permission checks only
 */
export const ModelFilesTab: React.FC<ModelFilesTabProps> = ({model}) => {
  const dispatch = useDispatch();

  // Use regular endpoint with filter
  const {data: files, load: loadFiles} = useModelFileTreeLoader(model?.uuid || '', FILTER_TAGS);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadType, setDownloadType] = useState<'all' | 'single'>('all');
  const [selectedFile, setSelectedFile] = useState<{uuid: string; name: string} | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (model?.uuid) {
      loadFiles();
    }
  }, [model?.uuid, loadFiles]);

  // Regular models: use canDownloadModelFiles
  const canDownload = canDownloadModelFiles(model as ModelDetailDTO);

  const handleDownloadAll = () => {
    setDownloadType('all');
    setSelectedFile(null);
    setDownloadModalVisible(true);
  };

  const handleDownloadFile = (fileUuid: string, fileName: string) => {
    setDownloadType('single');
    setSelectedFile({uuid: fileUuid, name: fileName});
    setDownloadModalVisible(true);
  };

  const handleCloseModal = () => {
    setDownloadModalVisible(false);
    setSelectedFile(null);
  };

  const handleConfirmDownload = () => {
    handleCloseModal();
    setIsDownloading(true);

    if (downloadType === 'all' && model?.uuid) {
      // Download all files in the model
      modelDownload(dispatch, model.uuid)
        .then((response) => downloadFromResponse(response.raw))
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    } else if (selectedFile && model?.uuid) {
      // Download individual file - track the download
      modelFileItemDownload(dispatch, model.uuid, selectedFile.uuid)
        .then((response) => {
          downloadFromResponse(response.raw);
          // Track individual file download
          trackModelItemDownloaded(model, selectedFile.name, selectedFile.uuid);
        })
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    }
  };

  if (!model) {
    return <div>No model selected</div>;
  }

  // Regular models: use children array directly
  const rootItems: TreeNode[] = (files as TreeNode[]) || [];
  const hasFiles = rootItems.length > 0;
  const mdpLink = model.metadata?.['model_development_plan'];
  const hasMdpLink = !!mdpLink && mdpLink.trim() !== '' && mdpLink.trim().toLowerCase() !== 'null';

  return (
    <div className={styles.modelFilesTab}>
      {/* Header with description and Download All button */}
      <div className={styles.header}>
        <span className={styles.description}>Browse and download model files, documentation, and adaptations</span>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadAll}
          loading={isDownloading}
          disabled={isDownloading || !hasFiles || canDownload !== PERMISSION.ALLOWED}
        >
          Download All
        </Button>
      </div>

      <Divider />

      {hasFiles ? (
        <>
          {/* Regular file tree - no Access column */}
          <FileTreeNode files={rootItems} isRoot={true} defaultFolderOpen={true} onDownloadFile={handleDownloadFile} />

          {/* MDP link if available */}
          {hasMdpLink && (
            <div style={{marginTop: '24px'}}>
              <strong>MDP: </strong>
              <a href={mdpLink} target="_blank" rel="noopener noreferrer">
                {mdpLink}
              </a>
            </div>
          )}
        </>
      ) : (
        <Flex vertical gap="10px" style={{alignItems: 'center', padding: '0 16px 16px'}}>
          <FolderOpenTwoTone twoToneColor={[styles.primaryTeal, styles.lockColor]} style={{fontSize: '39px'}} />
          <Typography.Text strong style={{fontSize: '16px'}}>
            No files available
          </Typography.Text>
          <Typography.Text type="secondary" style={{textAlign: 'center', maxWidth: '400px'}}>
            There are currently no files uploaded for this model entry.
          </Typography.Text>
        </Flex>
      )}

      {/* Download confirmation modal */}
      <ModelFileDownloadModal
        visible={downloadModalVisible}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDownload}
        model={model}
        downloadType={downloadType}
        fileName={selectedFile?.name}
      />
    </div>
  );
};
